import { useEffect, useState } from 'react';
import { Share2, Download, Bell } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useTranslation } from '../utils/useTranslation';

interface PWAUtilsProps {
    showInstallButton?: boolean;
    showShareButton?: boolean;
    showNotificationButton?: boolean;
}

export function PWAUtils({
    showInstallButton = true,
    showShareButton = true,
    showNotificationButton = false
}: PWAUtilsProps) {
    const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
    const [isInstallable, setIsInstallable] = useState(false);
    const [isStandalone, setIsStandalone] = useState(false);
    const { t } = useTranslation();

    useEffect(() => {
        // Detectar se está em modo standalone
        const standalone = window.matchMedia('(display-mode: standalone)').matches;
        setIsStandalone(standalone);

        // Capturar evento de instalação
        const handleBeforeInstallPrompt = (e: Event) => {
            e.preventDefault();
            setDeferredPrompt(e);
            setIsInstallable(true);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        // Detectar quando foi instalado
        const handleAppInstalled = () => {
            setIsInstallable(false);
            setDeferredPrompt(null);
            toast.success(t('pwa.install.success'));
        };

        window.addEventListener('appinstalled', handleAppInstalled);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            window.removeEventListener('appinstalled', handleAppInstalled);
        };
    }, []);

    const handleInstall = async () => {
        if (!deferredPrompt) {
            toast.error(t('pwa.install.error'));
            return;
        }

        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;

        if (outcome === 'accepted') {
            toast.success(t('pwa.install.inProgress'));
        }

        setDeferredPrompt(null);
        setIsInstallable(false);
    };

    const handleShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: 'Mystery Gift',
                    text: t('pwa.share.text'),
                    url: window.location.href
                });
                toast.success(t('pwa.share.success'));
            } catch (error) {
                if ((error as Error).name !== 'AbortError') {
                    toast.error(t('pwa.share.error'));
                }
            }
        } else {
            // Fallback: copiar link
            try {
                await navigator.clipboard.writeText(window.location.href);
                toast.success(t('pwa.share.fallbackCopySuccess'));
            } catch {
                toast.error(t('pwa.share.fallbackCopyError'));
            }
        }
    };

    const handleNotification = async () => {
        if (!('Notification' in window)) {
            toast.error(t('pwa.notifications.unsupported'));
            return;
        }

        const permission = await Notification.requestPermission();

        if (permission === 'granted') {
            toast.success(t('pwa.notifications.enabled'));

            // Mostrar notificação de teste
            new Notification('Mystery Gift', {
                body: t('pwa.notifications.preview'),
                icon: '/icons/icon-192x192.svg'
            });
        } else if (permission === 'denied') {
            toast.error(t('pwa.notifications.denied'));
        }
    };

    // Não mostrar botões se estiver em standalone e não for instalável
    if (isStandalone && !isInstallable) {
        return null;
    }

    return (
        <div className="flex gap-2 flex-wrap">
            {showInstallButton && isInstallable && (
                <Button
                    onClick={handleInstall}
                    variant="outline"
                    size="sm"
                    className="gap-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0 hover:from-purple-600 hover:to-pink-600"
                >
                    <Download className="w-4 h-4" />
                    {t('pwa.buttons.install')}
                </Button>
            )}

            {showShareButton && (
                <Button
                    onClick={handleShare}
                    variant="outline"
                    size="sm"
                    className="gap-2"
                >
                    <Share2 className="w-4 h-4" />
                    {t('pwa.buttons.share')}
                </Button>
            )}

            {showNotificationButton && (
                <Button
                    onClick={handleNotification}
                    variant="outline"
                    size="sm"
                    className="gap-2"
                >
                    <Bell className="w-4 h-4" />
                    {t('pwa.buttons.notify')}
                </Button>
            )}
        </div>
    );
}

// Hook personalizado para usar funcionalidades PWA
export function usePWA() {
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [isStandalone, setIsStandalone] = useState(false);

    useEffect(() => {
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        // Detectar modo standalone
        const standalone = window.matchMedia('(display-mode: standalone)').matches;
        setIsStandalone(standalone);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    const shareContent = async (data: ShareData) => {
        if (navigator.share) {
            try {
                await navigator.share(data);
                return true;
            } catch (error) {
                if ((error as Error).name !== 'AbortError') {
                    console.error('Erro ao compartilhar:', error);
                }
                return false;
            }
        }
        return false;
    };

    const requestNotifications = async () => {
        if ('Notification' in window) {
            const permission = await Notification.requestPermission();
            return permission === 'granted';
        }
        return false;
    };

    return {
        isOnline,
        isStandalone,
        shareContent,
        requestNotifications
    };
}
